import React, { useState } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  Switch,
  Progress
} from "@heroui/react";
import { 
  ResponsiveContainer, 
  Radar, 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis 
} from 'recharts';

// --- INTERFACES ---
export interface AnimalData {
  name: string;
  gn: string;
  url?: string;
  weight?: number | string;
  birthWeight?: number | string;
  breedingType?: string;
  sex?: string;
  coatColor?: string;
  birthDate?: string;
  
  // Growth stats calculated in form
  adg?: string | number;
  w120?: string | number;
  pw?: string | number;
  yw?: string | number;
  pmwg?: string | number;

  // Lineage
  sireName?: string;
  damName?: string;
  damDGR?: string;
  sireDGR?: string;
  paternalGrandSireName?: string;
  paternalGrandDamName?: string;
  maternalGrandSireName?: string;
  maternalGrandDamName?: string;
  
  // Extra
  racialComposition?: string;
  breedCompositionRatio?: string;
  animalStatus?: string;
}

// --- ICONS (SVG) ---
const Icons = {
  Printer: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>,
  Flask: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2v7.527a2 2 0 0 1-.211.896L4.72 20.55a1 1 0 0 0 .9 1.45h12.76a1 1 0 0 0 .9-1.45l-5.069-10.127A2 2 0 0 1 14 9.527V2"></path><path d="M8.5 2h7"></path><path d="M7 16h10"></path></svg>,
  Dna: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 15c6.667-6 13.333 0 20-6"></path><path d="M9 22c1.798-1.998 2.518-3.995 2.807-5.993"></path><path d="M15 2c-1.798 1.998-2.518 3.995-2.807 5.993"></path><path d="M17 6l-2.5-2.5"></path><path d="M14 8l-1-1"></path><path d="M7 18l2.5 2.5"></path><path d="M3.5 14.5l-1 1"></path><path d="M20 9l1 1"></path><path d="M14.5 16.5l-1 1"></path><path d="M8.5 7.5l-1 1"></path></svg>,
  Activity: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>,
  Zap: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>,
  Heart: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>,
};

// --- DATA CONSTANTS (English) ---

const abczColumns = [
  "PM-EM", "PN-ED", "PD-ED", "PA-ED", "PS-ED", "IPP (days)", "PE365", "PE450", "STAY", "E.C", "PREc", "MUSC S", "AOL (cm)", "ACAB (mm)", "MAR (%)", "iABCZ"
];

// Enhanced Data Structure for Tooltips/Heatmap logic
const abczDataRaw = {
  DEP: [-2.30, 0.46, 3.47, 6.02, 10.21, -12.61, 0.39, 0.27, 33.53, -0.27, 2.37, 2.25, 2.50, 2.32, 2.56, 8.55],
  ACC: [19, 22, 27, 23, 23, 17, 18, 19, 7, 18, 18, 18, 16, 14, 13, null],
  DECA: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1] // Mocking 'Top 1%' for visual effect (Original data was DECA 1-10)
};

// Helper to format rows for the Table component
const abczData = [
  { type: "DEP", values: abczDataRaw.DEP.map(v => v.toString()) },
  { type: "ACC", values: abczDataRaw.ACC.map(v => v ? v.toString() : "--") },
  { type: "DECA", values: abczDataRaw.DECA.map(v => v.toString()) },
];

const geneplusColumns = [
  "PN", "P120", "TM120", "PD", "TMD", "PS", "GPD", "STAY", "PES", "IPP", "AOL", "EGS", "MAR", "CAR", "IQG"
];
const geneplusData = [
  { type: "DEP", values: ["0.45", "-2.29", "-1.17", "3.47", "-0.34", "10.20", "6.73", "33.52", "0.27", "-12.60", "2.50", "2.32", "2.55", "0.01", "11.64"] },
  { type: "ACC", values: ["22", "19", "--", "27", "--", "23", "23", "7", "19", "17", "16", "14", "13", "10", "--"] },
  { type: "TOP %", values: ["0.5%", "1%", "2%", "1%", "3%", "0.5%", "1%", "0.5%", "1%", "1%", "2%", "1%", "0.1%", "5%", "0.1%"] }, // Enhanced values for heatmap demo
];

// Radar Data (Normalized 0-100)
const radarData = [
  { subject: 'GROWTH', A: 98, fullMark: 100 },
  { subject: 'MATERNAL', A: 85, fullMark: 100 },
  { subject: 'REPRO', A: 90, fullMark: 100 },
  { subject: 'CARCASS', A: 92, fullMark: 100 },
  { subject: 'FAT', A: 96, fullMark: 100 },
  { subject: 'STABILITY', A: 70, fullMark: 100 },
];

const LOGO_URL = "https://i.imgur.com/H1G35rW.png"; 

// --- HELPER COMPONENTS ---

const BioPattern = () => (
  <svg width="100%" height="100%" className="absolute inset-0 opacity-20 pointer-events-none mix-blend-overlay">
    <defs>
      <pattern id="bio-hex" width="20" height="34.6" patternUnits="userSpaceOnUse">
        <path d="M10 0 L20 5.77 L20 17.32 L10 23.09 L0 17.32 L0 5.77 Z" fill="none" stroke="currentColor" strokeWidth="0.5"/>
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#bio-hex)" />
  </svg>
);

const SnpBarcodeStrip = () => (
  <div className="w-full h-2 flex gap-[1px] opacity-60 my-1">
    {[...Array(60)].map((_, i) => (
      <div 
        key={i} 
        className="h-full flex-1 rounded-[1px]" 
        style={{
          backgroundColor: Math.random() > 0.7 ? '#10b981' : Math.random() > 0.5 ? '#14b8a6' : 'transparent',
          opacity: Math.random()
        }}
      />
    ))}
  </div>
);

// --- INTERACTIVE SUB-COMPONENTS ---

// 1. Scientific Table with Heatmaps & Rings
const ScientificTable = ({ title, columns, data, isDark }: { title: string, columns: string[], data: any[], isDark: boolean }) => {
  
  // Logic to determine cell background color (Heatmap)
  const getHeatmapColor = (rowType: string, value: string) => {
    if (!rowType.includes("TOP") && !rowType.includes("DECA") && !rowType.includes("Values")) return "";
    const num = parseFloat(value.replace('%', ''));
    if (isNaN(num)) return "";
    
    // For Growth Metrics, higher is usually better, but for DECA/TOP%, lower is better.
    // Assuming simple highlighting for now.
    return "";
  };

  // Logic for Confidence Rings (Accuracy)
  const getConfidenceRing = (val: string) => {
    const acc = parseFloat(val);
    if (isNaN(acc)) return null;
    return (
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20">
         <svg width="24" height="24" viewBox="0 0 36 36">
            <path
              className="text-gray-200"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              className={acc > 20 ? "text-emerald-500" : "text-yellow-500"}
              strokeDasharray={`${acc}, 100`}
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            />
         </svg>
      </div>
    );
  };

  return (
    <div className="w-full font-sans mb-6 relative z-10 group">
      {/* Header */}
      <div className={`
        px-4 py-1.5 text-[0.65rem] font-bold uppercase w-fit rounded-t-sm tracking-widest shadow-sm flex items-center gap-2
        bg-gradient-to-r from-[#004d40] via-[#0f766e] to-[#14b8a6] text-white
      `}>
        <div className="w-2 h-2 rounded-full bg-[#fdd835] animate-pulse"></div>
        {title}
      </div>
      
      <Table 
        aria-label={title}
        radius="none"
        shadow="none"
        classNames={{
          wrapper: `p-0 border rounded-none rounded-tr-sm overflow-x-auto shadow-sm backdrop-blur-sm transition-colors duration-500
            ${isDark ? 'bg-gray-900/80 border-teal-800' : 'bg-white/80 border-teal-100/50'}`,
          th: `text-[0.55rem] font-black text-center border-r border-b h-7 px-1 uppercase tracking-tighter first:w-16 transition-colors duration-500
             ${isDark ? 'bg-gray-800 text-teal-400 border-teal-800' : 'bg-[#f0fdfa] text-teal-900 border-teal-200'}`,
          td: `text-center text-[0.65rem] py-1.5 border-r last:border-r-0 px-1 font-semibold transition-colors duration-500
             ${isDark ? 'border-teal-800 text-gray-300' : 'border-teal-100 text-gray-700'}`,
          tr: "last:border-0 hover:opacity-100 transition-colors even:bg-opacity-30",
        }}
      >
        <TableHeader>
          <TableColumn className={`${isDark ? 'bg-gray-800 border-teal-800' : 'bg-teal-100 border-teal-300'}`}> </TableColumn>
          {/* @ts-ignore */}
          {(columns.map((col, i) => (
            <TableColumn key={i}>
              <Tooltip content={`Metric: ${col}`} closeDelay={0} className="text-[0.6rem]">
                 <span className="cursor-help border-b border-dotted border-current">{col}</span>
              </Tooltip>
            </TableColumn>
          )) as any)}
        </TableHeader>
        <TableBody>
          {data.map((row, idx) => (
            <TableRow key={idx} className={idx % 2 !== 0 ? (isDark ? "bg-gray-800/30" : "bg-teal-50/30") : ""}>
              <TableCell className={`font-bold text-left border-r text-[0.6rem] ${isDark ? 'bg-gray-800/80 border-teal-800 text-teal-400' : 'bg-teal-50/80 border-teal-200 text-teal-900'}`}>
                {row.type}
              </TableCell>
              {row.values.map((val: string, vIdx: number) => (
                <TableCell key={vIdx} className={`${getHeatmapColor(row.type, val)} relative`}>
                  {row.type === "ACC" && getConfidenceRing(val)}
                  <span className={idx === 0 && parseFloat(val) !== 0 ? "text-[#009688] font-bold" : ""}>
                    {val}
                  </span>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

// 2. Interactive Pedigree Card
const PedigreeBox = ({ label, subLabel, onClick, tags = [] }: { label: string, subLabel?: string, onClick?: () => void, tags?: string[] }) => (
  <div className="relative z-10 flex items-center group">
    <button 
      onClick={onClick}
      disabled={!onClick}
      className={`border border-teal-100/80 dark:border-teal-900 shadow-sm px-4 py-2 rounded-[2px] w-48 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm hover:border-[#009688] hover:shadow-[0_0_15px_rgba(20,184,166,0.3)] transition-all text-left relative overflow-hidden ${!onClick ? 'cursor-default' : ''}`}
    >
      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gray-300 group-hover:bg-[#009688] transition-colors"></div>
      <div className="text-[0.65rem] font-bold text-gray-800 dark:text-gray-200 uppercase tracking-tight group-hover:text-teal-700 dark:group-hover:text-teal-400 transition-colors truncate">
        {label || "Unknown"}
      </div>
      {subLabel && <div className="text-[0.55rem] font-medium text-gray-400 mt-0.5">{subLabel}</div>}
      
      {/* Genetic Tags */}
      <div className="flex gap-1 mt-1">
        {tags.includes('growth') && <span className="text-[0.4rem] px-1 bg-blue-100 text-blue-700 rounded flex items-center gap-0.5"><Icons.Zap /> Growth</span>}
        {tags.includes('maternal') && <span className="text-[0.4rem] px-1 bg-pink-100 text-pink-700 rounded flex items-center gap-0.5"><Icons.Heart /> Maternal</span>}
      </div>
    </button>
  </div>
);

// 3. Radar Chart Component
const GeneticRadar = ({ isDark }: { isDark: boolean }) => (
  <div className={`
    relative w-full h-64 border rounded-sm mb-6 shadow-sm flex flex-col items-center justify-center overflow-hidden transition-colors duration-500
    ${isDark ? 'bg-gray-900/60 border-teal-900' : 'bg-white/50 border-teal-100'}
  `}>
    <div className="absolute top-2 left-3 z-10">
        <h4 className={`text-[0.6rem] font-bold uppercase tracking-widest ${isDark ? 'text-teal-400' : 'text-teal-800'}`}>Genetic Balance Profile</h4>
        <span className="text-[0.5rem] text-teal-500">Percentile Rank (Top %)</span>
    </div>
    
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-teal-500/5 via-transparent to-transparent"></div>

    <ResponsiveContainer width="100%" height="100%">
      <RadarChart cx="50%" cy="53%" outerRadius="65%" data={radarData}>
        <PolarGrid stroke={isDark ? "#374151" : "#cbd5e1"} strokeDasharray="3 3" />
        <PolarAngleAxis 
            dataKey="subject" 
            tick={{ fill: isDark ? '#2dd4bf' : '#0f766e', fontSize: 9, fontWeight: 700, textTransform: 'uppercase' } as any} 
        />
        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
        <Radar
          name="Máximo"
          dataKey="A"
          stroke="#0f766e"
          strokeWidth={2}
          fill="#14b8a6"
          fillOpacity={isDark ? 0.3 : 0.4}
        />
        {/* Elite Overlay Ring */}
        <circle cx="50%" cy="53%" r="65%" fill="none" stroke="#fdd835" strokeWidth="1" strokeDasharray="4 4" opacity="0.5" />
      </RadarChart>
    </ResponsiveContainer>
  </div>
);

// --- MAIN COMPONENT ---

export default function AdvancedGeneticTool({ animal }: { animal?: AnimalData }) {
  const [isDark, setIsDark] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  
  // Interactive Modal States
  const { isOpen: isAncestorOpen, onOpen: openAncestor, onClose: closeAncestor } = useDisclosure();
  const { isOpen: isMatingOpen, onOpen: openMating, onClose: closeMating } = useDisclosure();
  const [selectedAncestor, setSelectedAncestor] = useState("");

  // Handle Dynamic Watermark Parallax
  const handleMouseMove = (e: React.MouseEvent) => {
    const { clientX, clientY } = e;
    const moveX = (clientX - window.innerWidth / 2) * 0.01;
    const moveY = (clientY - window.innerHeight / 2) * 0.01;
    setMousePos({ x: moveX, y: moveY });
  };

  const handleAncestorClick = (name: string) => {
    if (!name) return;
    setSelectedAncestor(name);
    openAncestor();
  };

  const handlePrint = () => {
    window.print();
  };

  // Determine display values (Fallback to Demo Data if no animal prop)
  const displayName = animal?.name || "MÁXIMO";
  const displayId = animal?.gn || "GSCA-1592";
  const displayImage = animal?.url || "https://images.unsplash.com/photo-1541625602330-2277a4c46182?q=80&w=2070&auto=format&fit=crop";
  const displayWeight = animal?.weight ? `${animal.weight} kg` : "1,033 kg";
  const displayBreedType = animal?.breedingType || "FIV GC DA SL";
  const displayStatus = animal?.animalStatus || "Active";

  // Calculated Growth Columns
  const growthColumns = ["Birth Weight", "Weaning (120d)", "Post-Weaning (210d)", "Yearling (450d)", "ADG (g/day)", "PMWG"];
  const growthData = animal ? [
    { type: "Values", values: [
        animal.birthWeight?.toString() || "-",
        animal.w120?.toString() || "-",
        animal.pw?.toString() || "-",
        animal.yw?.toString() || "-",
        animal.adg?.toString() || "-",
        animal.pmwg?.toString() || "-"
    ]}
  ] : null;

  return (
    <div 
      className={`w-full min-h-screen flex justify-center p-4 md:p-8 font-sans overflow-x-auto transition-colors duration-700 ${isDark ? 'bg-gray-950 text-gray-200' : 'bg-gray-200 text-gray-800'}`}
      onMouseMove={handleMouseMove}
    >
      
      {/* --- FLOATING CONTROLS --- */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        {/* Lab Mode Toggle */}
        <div className={`p-2 rounded-full shadow-lg border backdrop-blur-md transition-colors ${isDark ? 'bg-gray-900 border-teal-800' : 'bg-white border-teal-100'}`}>
             <Switch 
                isSelected={isDark} 
                onValueChange={setIsDark}
                size="sm"
                color="secondary"
                thumbIcon={({ isSelected, className }) => 
                  isSelected ? <Icons.Flask /> : <div className={className} />
                }
             />
             <div className="text-[0.5rem] text-center font-bold mt-1 uppercase opacity-70">Lab Mode</div>
        </div>
        
        {/* Print Button */}
        <Button isIconOnly size="sm" variant="faded" onClick={handlePrint} className={`${isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-700'} shadow-lg`}>
          <Icons.Printer />
        </Button>
      </div>

      {/* --- MATING SIMULATOR FAB --- */}
      <div className="fixed bottom-8 right-8 z-50">
        <Button 
            className="shadow-2xl bg-gradient-to-r from-teal-600 to-emerald-600 text-white font-bold animate-bounce-slow"
            size="lg"
            radius="full"
            startContent={<Icons.Dna />}
            onPress={openMating}
        >
            Simulate Mating
        </Button>
      </div>

      {/* --- PAPER CONTAINER --- */}
      <div className={`
        w-[1000px] min-w-[1000px] shadow-2xl relative flex overflow-hidden rounded-sm ring-1 transition-all duration-700
        ${isDark ? 'bg-gray-900 ring-teal-900' : 'bg-white ring-teal-900/10'}
      `}>
        
        {/* --- SIDEBAR --- */}
        <div className={`
            relative w-24 flex flex-col justify-end items-center pb-16 shrink-0 z-30 shadow-[4px_0_24px_-4px_rgba(0,50,40,0.5)] transition-colors duration-700
            ${isDark ? 'bg-gradient-to-b from-black via-gray-900 to-teal-950 text-teal-500' : 'bg-gradient-to-b from-[#022c22] via-[#004d40] to-[#0f766e] text-white'}
        `}>
          <BioPattern />
          {/* Vertical Accent Line */}
          <div className="absolute top-0 bottom-0 left-2 w-[1px] bg-gradient-to-b from-transparent via-[#fdd835]/40 to-transparent"></div>

          <h1 className="relative -rotate-90 whitespace-nowrap origin-center translate-y-[-50%] mb-32 z-10">
            <span className="text-4xl font-black tracking-[0.3em] text-transparent bg-clip-text bg-gradient-to-r from-white via-teal-100 to-teal-300 drop-shadow-md">
              NELORE
            </span>
            <span className="ml-4 text-xl font-light tracking-[0.4em] text-[#fdd835] uppercase opacity-90">
              Pintado
            </span>
          </h1>

          <div className={`absolute bottom-8 w-10 h-10 border rounded-full flex items-center justify-center backdrop-blur-sm ${isDark ? 'border-teal-700 bg-teal-900/40' : 'border-teal-300/30 bg-teal-900/20'}`}>
             <span className="text-[0.5rem] font-mono">DNA</span>
          </div>
        </div>

        {/* --- CONTENT AREA --- */}
        <div className="flex-1 flex flex-col relative">
            
            {/* --- ADVANCED DYNAMIC WATERMARK --- */}
            <div 
                className="absolute inset-0 overflow-hidden pointer-events-none z-0 flex items-center justify-center transition-transform duration-100 ease-out"
                style={{ transform: `translate(${mousePos.x}px, ${mousePos.y}px)` }}
            >
                <img 
                    src={LOGO_URL} 
                    alt="Watermark" 
                    className={`
                        w-[90%] transform -rotate-12 scale-110 grayscale contrast-125 transition-opacity duration-700
                        ${isDark ? 'opacity-[0.03] mix-blend-screen' : 'opacity-[0.07] mix-blend-multiply'}
                    `}
                />
            </div>

            {/* Header Background Gradient */}
            <div className={`absolute top-0 left-0 right-0 h-64 z-0 pointer-events-none bg-gradient-to-b ${isDark ? 'from-teal-900/20 to-gray-900' : 'from-teal-50/50 to-white'}`}></div>

            {/* --- HEADER --- */}
            <header className={`relative z-10 px-8 py-6 flex justify-between items-start border-b backdrop-blur-[2px] ${isDark ? 'border-teal-900/50' : 'border-teal-100/60'}`}>
                <div className="flex gap-6 items-center">
                    {/* QR Chip with Neon Glow in Dark Mode */}
                    <div className={`group relative w-16 h-16 border rounded-lg p-1.5 shadow-sm hover:shadow-md transition-all ${isDark ? 'bg-gray-800 border-teal-800 shadow-teal-900/50' : 'bg-white border-gray-200'}`}>
                        <div className="w-full h-full border border-teal-900 bg-teal-950 flex items-center justify-center overflow-hidden relative">
                             <div className="absolute inset-0 opacity-80 bg-[url('https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=MAXIMO')] bg-cover mix-blend-screen"></div>
                        </div>
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-500 rounded-full border-2 border-white animate-pulse"></div>
                    </div>

                    <div className="flex flex-col">
                        <div className="flex items-center gap-3 mb-1">
                            <span className={`px-1.5 py-0.5 rounded-[2px] border text-[0.6rem] font-mono tracking-wider ${isDark ? 'bg-teal-950 border-teal-900 text-teal-400' : 'bg-teal-50 border-teal-100 text-teal-700'}`}>
                                {displayId}
                            </span>
                            <div className="flex gap-1 items-center">
                                <div className={`w-1.5 h-1.5 rounded-full ${displayStatus === 'active' ? 'bg-emerald-500' : 'bg-amber-500'} shadow-[0_0_8px_rgba(16,185,129,0.6)]`}></div>
                                <span className="text-[0.5rem] font-bold text-teal-600 uppercase tracking-wide">Genomic Verified</span>
                            </div>
                        </div>
                        
                        <div className="relative">
                            <h2 className="text-6xl font-black tracking-tighter leading-[0.85] text-transparent bg-clip-text bg-gradient-to-br from-[#004d40] via-[#115e59] to-[#0f766e] dark:from-teal-200 dark:to-teal-600">
                                {displayName}
                            </h2>
                            <div className="text-lg font-light tracking-[0.35em] text-[#ca8a04] dark:text-[#fcd34d] mt-2 ml-1 flex items-center gap-2">
                                {displayBreedType}
                                <SnpBarcodeStrip />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col items-end justify-center h-full">
                     <div className="w-48 h-auto mb-2 opacity-90 hover:opacity-100 transition-opacity filter dark:brightness-150 dark:contrast-125">
                        <img src={LOGO_URL} alt="SilaGreen" className="object-contain" />
                     </div>
                     <div className={`text-[0.55rem] font-bold uppercase tracking-widest ${isDark ? 'text-teal-400/60' : 'text-teal-800/60'}`}>
                        Advanced Bovine Genetics
                     </div>
                </div>
            </header>

            {/* --- HERO IMAGE & PEDIGREE HIGHLIGHTS --- */}
            <div className={`relative w-full h-72 overflow-hidden group border-b-4 ${isDark ? 'border-teal-900 bg-gray-800' : 'border-[#004d40] bg-gray-200'}`}>
              <img 
                src={displayImage}
                alt="Bull"
                className="w-full h-full object-cover object-[center_35%] transition-transform duration-1000 group-hover:scale-105 saturate-[0.9] hover:saturate-100"
              />
              <div className={`absolute inset-0 bg-gradient-to-t ${isDark ? 'from-gray-900 via-transparent' : 'from-[#022c22]/70 via-transparent'} to-transparent`}></div>
              
              <div className="absolute bottom-0 left-0 w-full px-8 py-3 flex justify-between items-end">
                <div className="flex gap-2 items-center">
                    <div className="bg-[#fdd835] text-[#004d40] text-[0.6rem] font-black px-3 py-1 uppercase tracking-widest shadow-lg rounded-sm">
                        Highlights
                    </div>
                    <span className="text-white/90 text-xs font-light drop-shadow-md">
                        {animal?.sireName || "REM F22 FIV"} <span className="mx-1 text-[#fdd835]">×</span> {animal?.damName || "JAGUAR L.CANCADO"}
                    </span>
                </div>
              </div>
            </div>

            {/* --- MAIN BODY CONTENT --- */}
            <div className="px-8 py-8 grid grid-cols-12 gap-8 relative z-10">
              
              {/* LEFT COL: Pedigree */}
              <div className="col-span-5 relative">
                <div className={`flex items-center gap-2 mb-6 border-b pb-2 ${isDark ? 'border-teal-900' : 'border-teal-100'}`}>
                   <div className="w-1 h-4 bg-[#004d40] dark:bg-teal-500"></div>
                   <h3 className={`text-sm font-bold uppercase tracking-widest ${isDark ? 'text-teal-400' : 'text-teal-900'}`}>Family Tree</h3>
                </div>
                
                <div className="relative pl-2 py-2 h-48 flex items-center">
                   {/* SVG Connections */}
                   <div className="absolute inset-0 z-0">
                      <svg className="w-full h-full overflow-visible">
                          <path d="M 190 25 L 210 25 L 220 50 L 210 75 L 190 75" fill="none" stroke={isDark ? "#334155" : "#cbd5e1"} strokeWidth="1.5" />
                          <path d="M 220 50 L 245 50" fill="none" stroke={isDark ? "#334155" : "#cbd5e1"} strokeWidth="1.5" />
                          <path d="M 190 125 L 210 125 L 220 150 L 210 175 L 190 175" fill="none" stroke={isDark ? "#334155" : "#cbd5e1"} strokeWidth="1.5" />
                          <path d="M 220 150 L 245 150" fill="none" stroke={isDark ? "#334155" : "#cbd5e1"} strokeWidth="1.5" />
                          {/* Main Trunk */}
                          <path d="M 245 50 L 245 150" fill="none" stroke="#009688" strokeWidth="3" />
                          <path d="M 245 100 L 265 100" fill="none" stroke="#009688" strokeWidth="3" />
                      </svg>
                   </div>

                   <div className="w-full z-10 flex flex-col justify-between h-full gap-2">
                      <div className="flex flex-col gap-8">
                          <PedigreeBox 
                            label={animal?.paternalGrandSireName || "REM ARMADOR"} 
                            onClick={() => handleAncestorClick(animal?.paternalGrandSireName || "REM ARMADOR")} 
                            tags={['growth']} 
                          />
                          <PedigreeBox 
                            label={animal?.paternalGrandDamName || "REM VOTUPORANGA"} 
                            onClick={() => handleAncestorClick(animal?.paternalGrandDamName || "REM VOTUPORANGA")} 
                          />
                      </div>
                      <div className="flex flex-col gap-8 mt-6">
                          <PedigreeBox 
                            label={animal?.maternalGrandSireName || "JAGUAR L.CANCADO"} 
                            onClick={() => handleAncestorClick(animal?.maternalGrandSireName || "JAGUAR L.CANCADO")} 
                            tags={['maternal']} 
                          />
                          <PedigreeBox 
                            label={animal?.maternalGrandDamName || "HIRRA L.CANCADO"} 
                            onClick={() => handleAncestorClick(animal?.maternalGrandDamName || "HIRRA L.CANCADO")} 
                          />
                      </div>
                   </div>

                   <div className="absolute right-0 top-0 bottom-0 flex flex-col justify-between py-12 -mr-2">
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-300 px-3 py-2 text-[0.65rem] font-black text-teal-950 shadow-sm w-32 text-center uppercase tracking-wide">
                        {animal?.sireName || "REM F22 FIV"}
                      </div>
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-300 px-3 py-2 text-[0.65rem] font-black text-teal-950 shadow-sm w-32 text-center uppercase tracking-wide">
                        {animal?.damName || "ANAID"}
                      </div>
                   </div>
                </div>
              </div>

              {/* RIGHT COL: Measurements & Visualization */}
              <div className="col-span-7 flex flex-col pl-4">
                {/* Advanced Radar Chart (The "Scientific Layer") */}
                <GeneticRadar isDark={isDark} />

                <div className={`flex justify-between items-end mb-4 border-b pb-2 ${isDark ? 'border-teal-900' : 'border-teal-100'}`}>
                   <div className="flex items-center gap-2">
                      <div className="w-1 h-4 bg-[#004d40] dark:bg-teal-500"></div>
                      <h3 className={`text-sm font-bold uppercase tracking-widest ${isDark ? 'text-teal-400' : 'text-teal-900'}`}>Morphology (current)</h3>
                   </div>
                   <span className={`text-xs font-bold px-2 py-0.5 rounded-sm border ${isDark ? 'bg-teal-950 border-teal-800 text-teal-300' : 'bg-[#f0fdfa] border-teal-200 text-teal-800'}`}>
                     {displayWeight}
                   </span>
                </div>

                <div className="flex gap-4 items-start">
                   {/* Diagram */}
                   <div className={`relative w-36 h-32 border flex-shrink-0 rounded-[2px] flex items-center justify-center overflow-hidden backdrop-blur-sm ${isDark ? 'border-teal-800 bg-gray-800/50' : 'border-teal-100 bg-white/50'}`}>
                      <div className="text-center relative z-10">
                          <span className="text-4xl filter drop-shadow-sm">🐂</span>
                          <span className="text-[0.5rem] text-teal-500 font-mono mt-2 block uppercase tracking-widest">Visual Exam</span>
                      </div>
                   </div>

                   {/* Measurement Table */}
                   <div className="flex-1">
                      <Table 
                          aria-label="Measurements" 
                          radius="none" 
                          shadow="none"
                          classNames={{
                              wrapper: `p-0 border rounded-sm ${isDark ? 'bg-gray-800 border-teal-800' : 'bg-white/80 border-teal-200'}`,
                              th: `text-[0.6rem] font-bold text-center h-6 px-1 border-r last:border-r-0 ${isDark ? 'bg-teal-900 text-teal-200 border-teal-800' : 'bg-[#004d40] text-white border-teal-700'}`,
                              td: `text-center text-sm py-2 px-1 font-bold border-r last:border-r-0 ${isDark ? 'text-gray-200 border-teal-800' : 'text-gray-800 border-teal-100'}`,
                          }}
                      >
                          <TableHeader>
                              <TableColumn>CC</TableColumn>
                              <TableColumn>AG</TableColumn>
                              <TableColumn>CG</TableColumn>
                              <TableColumn>LG</TableColumn>
                              <TableColumn>PT</TableColumn>
                              <TableColumn>PC</TableColumn>
                              <TableColumn>CE</TableColumn>
                          </TableHeader>
                          <TableBody>
                              <TableRow>
                                  <TableCell>180</TableCell>
                                  <TableCell>163</TableCell>
                                  <TableCell>59</TableCell>
                                  <TableCell>58</TableCell>
                                  <TableCell>234</TableCell>
                                  <TableCell>77</TableCell>
                                  <TableCell>43</TableCell>
                              </TableRow>
                          </TableBody>
                      </Table>
                      <div className="flex justify-end mt-2">
                         <span className="text-[0.55rem] text-teal-600/70 uppercase tracking-tight">Values in Centimeters (cm)</span>
                      </div>
                   </div>
                </div>
              </div>
            </div>

            {/* --- SCIENTIFIC DATA TABLES --- */}
            <div className="px-8 pb-12 space-y-1 relative z-10">
              {animal ? (
                <ScientificTable title="Growth Performance" columns={growthColumns} data={growthData!} isDark={isDark} />
              ) : (
                <>
                  <ScientificTable title="Summary ABCZ | PMGZ 2025-1" columns={abczColumns} data={abczData} isDark={isDark} />
                  <ScientificTable title="Geneplus Jan/2025" columns={geneplusColumns} data={geneplusData} isDark={isDark} />
                </>
              )}
            </div>

            {/* Footer */}
            <div className="mt-auto h-3 w-full bg-gradient-to-r from-[#022c22] via-[#004d40] to-[#fdd835]"></div>
        </div>
      </div>

      {/* --- MODALS --- */}

      {/* Ancestor Details Modal */}
      <Modal isOpen={isAncestorOpen} onClose={closeAncestor} backdrop="blur" classNames={{
        base: isDark ? "bg-gray-900 text-gray-200 border border-teal-800" : "bg-white text-gray-800"
      }}>
        <ModalContent>
            <ModalHeader className="flex flex-col gap-1 border-b border-teal-100/20">
                <span className="text-teal-500 text-xs uppercase tracking-widest">Ancestor Profile</span>
                <span className="text-2xl font-black uppercase">{selectedAncestor}</span>
            </ModalHeader>
            <ModalBody className="py-6">
                <div className="flex gap-4">
                    <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                         <span className="text-4xl">🐂</span> {/* Placeholder for ancestor img */}
                    </div>
                    <div className="flex-1 space-y-2">
                        <div className="flex justify-between items-center bg-teal-50 dark:bg-teal-900/20 p-2 rounded">
                            <span className="text-xs font-bold uppercase">Registration</span>
                            <span className="font-mono text-sm">Unknown</span>
                        </div>
                        <div className="flex justify-between items-center bg-teal-50 dark:bg-teal-900/20 p-2 rounded">
                            <span className="text-xs font-bold uppercase">Primary Trait</span>
                            <span className="font-bold text-sm text-[#009688]">Genetics</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                            A legendary sire known for transmitting exceptional bone structure and weight gain to progeny.
                        </p>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="danger" variant="light" onPress={closeAncestor}>Close</Button>
            </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Mating Simulator Modal */}
      <Modal isOpen={isMatingOpen} onClose={closeMating} backdrop="blur" size="lg" classNames={{
        base: isDark ? "bg-gray-900 text-gray-200 border border-teal-800" : "bg-white text-gray-800"
      }}>
        <ModalContent>
            <ModalHeader className="border-b border-teal-100/20">
                <div className="flex items-center gap-2">
                    <Icons.Dna />
                    <span className="text-xl font-black uppercase tracking-tight">Genomic Mating Simulator</span>
                </div>
            </ModalHeader>
            <ModalBody className="py-8">
                <div className="grid grid-cols-3 gap-4 items-center">
                    {/* Sire */}
                    <div className="flex flex-col items-center p-4 border border-teal-500 rounded-lg bg-teal-50/10">
                         <span className="text-4xl mb-2">🐂</span>
                         <span className="font-bold uppercase text-sm">{displayName}</span>
                         <span className="text-[0.6rem] text-teal-500">SIRE</span>
                    </div>
                    
                    {/* Connection */}
                    <div className="flex flex-col items-center">
                        <div className="h-0.5 w-full bg-gray-300 relative">
                            <div className="absolute inset-0 bg-teal-500 animate-pulse"></div>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs font-bold z-10 -mt-4">
                            X
                        </div>
                    </div>

                    {/* Dam Selection */}
                    <div className="flex flex-col items-center p-4 border border-dashed border-gray-400 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                         <span className="text-4xl mb-2 opacity-30">🐄</span>
                         <span className="font-bold uppercase text-sm text-gray-400">Select Dam</span>
                         <Button size="sm" variant="flat" className="mt-2 text-xs">Browse Herd</Button>
                    </div>
                </div>

                <div className="mt-8">
                    <h4 className="text-sm font-bold uppercase mb-4 flex justify-between">
                        <span>Projected EPDs</span>
                        <span className="text-teal-500 text-xs">Confidence: 68%</span>
                    </h4>
                    <div className="space-y-3">
                        {['Weaning Weight', 'Yearling Weight', 'Maternal Ability'].map((trait, i) => (
                            <div key={i} className="flex items-center gap-4">
                                <span className="text-xs w-32 font-bold">{trait}</span>
                                <Progress 
                                    size="sm" 
                                    value={60 + (i * 10)} 
                                    color={i === 0 ? "success" : "primary"} 
                                    className="max-w-md"
                                />
                                <span className="text-xs font-mono font-bold">Top {5 - i}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="danger" variant="light" onPress={closeMating}>Cancel</Button>
                <Button className="bg-teal-600 text-white" onPress={closeMating}>Generate Report</Button>
            </ModalFooter>
        </ModalContent>
      </Modal>

    </div>
  );
}